// src/context/PlanContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";

const PlanContext = createContext();

export const usePlan = () => useContext(PlanContext);

export const PlanProvider = ({ children }) => {
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchPlanData = async () => {
      if (!user) {
        setPlanData(null);
        setLoading(false);
        return;
      }

      try {
        console.log("Fetching plan data for user:", user.uid, user.email);

        // Try fetching by user UID first (which appears to be how your data is structured)
        const planDocRef = doc(db, "userPlans", user.uid);
        const planDoc = await getDoc(planDocRef);

        console.log("Plan document exists?", planDoc.exists());

        if (planDoc.exists()) {
          const data = planDoc.data();
          console.log("Plan data loaded:", data);
          setPlanData(data);
        } else {
          // Special case for test accounts
          if (user.email.includes("pro") && user.email.includes("test.com")) {
            console.log("Special handling for test account:", user.email);
            setPlanData({
              planType: "pro",
              maxAccounts: 20,
              maxStorage: 5 * 1024 * 1024 * 1024, // 5GB
              features: {
                customizableDashboard: true,
                aiTradingMentor: true,
                enhancedJournal: true,
                advancedAnalytics: true,
                csvImporting: true,
                tradeCategorizing: true,
              },
            });
          } else {
            // Default to standard plan if no plan data exists
            console.log("No plan found, defaulting to standard");
            setPlanData({
              planType: "standard",
              maxAccounts: 1,
              maxStorage: 1073741824, // 1GB
              features: {
                customizableDashboard: false,
                aiTradingMentor: false,
                enhancedJournal: true,
                advancedAnalytics: true,
                csvImporting: true,
                tradeCategorizing: true,
              },
            });
          }
        }
      } catch (error) {
        console.error("Error fetching plan data:", error);
        setPlanData({
          planType: "standard", // Default to standard on error
          maxAccounts: 1,
          maxStorage: 1073741824,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPlanData();
  }, [user]);

  const hasFeature = (featureName) => {
    if (!planData || !planData.features) return false;
    return planData.features[featureName] === true;
  };

  const isPro = () => {
    console.log("isPro() called, with plan data:", planData);
    return planData?.planType === "pro";
  };

  return (
    <PlanContext.Provider value={{ planData, loading, hasFeature, isPro }}>
      {children}
    </PlanContext.Provider>
  );
};
